import React, { useEffect, useMemo, useState } from "react";
import axios from "axios";
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import {
  AddRoundedIcon,
  EditOutlinedIcon,
  DeleteOutlineOutlinedIcon,
  ExpandMoreRoundedIcon,
} from "../../utilities/icons";
import { ToastContainer, toast } from "react-toastify";
import Cookies from "js-cookie";
import { projectsApi } from "../../apiCalls/Apicalls";

function initials(name) {
  return name
    .split(" ")
    .map((part) => part[0])
    .join("")
    .slice(0, 2)
    .toUpperCase();
}

const emptyForm = {
  name: "",
  code: "",
  description: "",
  roles: "",
};

function Projects() {
  const [projects, setProjects] = useState([]);
  const [open, setOpen] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [form, setForm] = useState(emptyForm);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [deletingProjectId, setDeletingProjectId] = useState(null);
  const [error, setError] = useState("");
  const [expandedCards, setExpandedCards] = useState({});

  const sortedProjects = useMemo(
    () => [...projects].sort((a, b) => a.name.localeCompare(b.name)),
    [projects],
  );

  const fetchProjects = async () => {
    try {
      setLoading(true);
      setError("");
      const response = await axios.get(projectsApi);
      setProjects(response.data || []);
    } catch (fetchError) {
      console.error("Error fetching projects:", fetchError);
      setError("Unable to load projects from backend.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchProjects();
  }, []);

  const closeDialog = () => {
    setOpen(false);
    setEditingId(null);
    setForm(emptyForm);
  };

  const openCreate = () => {
    setEditingId(null);
    setForm(emptyForm);
    setOpen(true);
  };

  const openEdit = (project) => {
    setEditingId(project.id);
    setForm({
      name: project.name || "",
      code: project.code || "",
      description: project.description || "",
      roles: Array.isArray(project.roles) ? project.roles.join(", ") : "",
    });
    setOpen(true);
  };

  const handleSave = async () => {
    const config = {
      headers: {
        Authorization: Cookies.get("jwtToken"),
        "Content-Type": "application/json",
      },
    };

    const payload = {
      name: form.name.trim(),
      code: form.code.trim(),
      description: form.description.trim(),
      roles: form.roles
        .split(",")
        .map((item) => item.trim())
        .filter(Boolean),
      members:
        editingId &&
        projects.find((project) => project.id === editingId)?.members?.length
          ? projects.find((project) => project.id === editingId).members
          : ["Admin Team"],
      colorClass:
        projects.find((project) => project.id === editingId)?.colorClass ||
        "c1",
      health:
        projects.find((project) => project.id === editingId)?.health ||
        "Growing",
    };

    if (!payload.name || !payload.code) {
      toast.error("Project name and code are required");
      return;
    }

    try {
      setSaving(true);
      if (editingId) {
        const response = await axios.put(
          `${projectsApi}/${editingId}`,
          payload,
          config,
        );
        setProjects((previous) =>
          previous.map((project) =>
            project.id === editingId ? response.data : project,
          ),
        );
        toast.success("Project updated successfully");
      } else {
        const response = await axios.post(projectsApi, payload, config);
        setProjects((previous) => [...previous, response.data]);
        toast.success("Project created successfully");
      }
      closeDialog();
    } catch (saveError) {
      console.error("Error saving project:", saveError);
      toast.error(saveError.response?.data?.error || "Unable to save project");
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (projectId) => {
    if (deletingProjectId === projectId) {
      return;
    }

    try {
      setDeletingProjectId(projectId);
      await axios.delete(`${projectsApi}/${projectId}`, {
        headers: {
          Authorization: Cookies.get("jwtToken"),
          "Content-Type": "application/json",
        },
      });
      setProjects((previous) =>
        previous.filter((project) => project.id !== projectId),
      );
      toast.success("Project deleted successfully");
    } catch (deleteError) {
      console.error("Error deleting project:", deleteError);
      toast.error(
        deleteError.response?.data?.error || "Unable to delete project",
      );
    } finally {
      setDeletingProjectId(null);
    }
  };

  const toggleRoles = (projectId) => {
    setExpandedCards((previous) => ({
      ...previous,
      [projectId]: !previous[projectId],
    }));
  };

  return (
    <>
      <div className="ax-page-grid">
        <div className="ax-section-head">
          <div>
            <Typography
              sx={{
                fontFamily: "var(--ax-font-heading)",
                fontWeight: 500,
                fontSize: "1.05rem",
                letterSpacing: "-0.02em",
                color: "var(--ax-ink)",
              }}
            >
              {sortedProjects.length} project
              {sortedProjects.length === 1 ? "" : "s"}
            </Typography>
            <Typography
              sx={{ color: "var(--ax-ink-2)", fontSize: "0.84rem", mt: 0.5 }}
            >
              Manage workspaces, roles, and access mappings
            </Typography>
          </div>
          <Button
            variant="contained"
            startIcon={<AddRoundedIcon fontSize="small" />}
            onClick={openCreate}
          >
            New Project
          </Button>
        </div>

        {error ? (
          <Alert severity="error" sx={{ borderRadius: "var(--ax-radius-md)" }}>
            {error}
          </Alert>
        ) : null}

        {loading ? (
          <Box
            className="ax-card"
            sx={{ minHeight: 220, display: "grid", placeItems: "center" }}
          >
            <Stack spacing={2} alignItems="center">
              <CircularProgress size={28} />
              <Typography
                sx={{ color: "var(--ax-ink-2)", fontSize: "0.86rem" }}
              >
                Loading projects from backend...
              </Typography>
            </Stack>
          </Box>
        ) : (
          <div className="ax-projects-grid">
            {sortedProjects.map((project) => (
              <div
                key={project.id}
                className="ax-card ax-project-card"
              >
                <Stack
                  direction="row"
                  justifyContent="space-between"
                  alignItems="flex-start"
                  spacing={2}
                >
                  <div>
                    <h3>{project.name}</h3>
                    <Typography
                      sx={{
                        color: "var(--ax-ink-2)",
                        fontSize: "0.78rem",
                        fontWeight: 500,
                        textTransform: "uppercase",
                        letterSpacing: "0.05em",
                        mt: 0.25,
                      }}
                    >
                      {project.code}
                    </Typography>
                  </div>
                  <span className="ax-chip green">{project.health}</span>
                </Stack>

                <p>{project.description}</p>

                <div className="ax-project-spacer" />

                <div className="ax-project-roles-shell">
                  <button
                    type="button"
                    className={`ax-roles-toggle${
                      expandedCards[project.id] ? " is-open" : ""
                    }`}
                    onClick={() => toggleRoles(project.id)}
                  >
                    <span>Roles ({(project.roles || []).length})</span>
                    <ExpandMoreRoundedIcon fontSize="small" />
                  </button>

                  {expandedCards[project.id] ? (
                    <div className="ax-role-list ax-role-list-panel">
                      {(project.roles || []).map((role) => (
                        <span key={role} className="ax-pill">
                          {role}
                        </span>
                      ))}
                    </div>
                  ) : null}
                </div>

                <div className="ax-project-footer">
                  <div className="ax-avatars">
                    {(project.members || []).slice(0, 4).map((member) => (
                      <span key={member}>{initials(member)}</span>
                    ))}
                  </div>
                  <Stack
                    direction="row"
                    spacing={0.5}
                    className="ax-project-actions"
                  >
                    <IconButton
                      onClick={() => openEdit(project)}
                      disabled={saving || deletingProjectId === project.id}
                      size="small"
                      sx={{
                        color: "var(--ax-ink-2)",
                        "&:hover": {
                          color: "var(--ax-blue)",
                          bgcolor: "var(--ax-blue-overlay)",
                        },
                      }}
                    >
                      <EditOutlinedIcon fontSize="small" />
                    </IconButton>
                    <IconButton
                      onClick={() => handleDelete(project.id)}
                      disabled={deletingProjectId === project.id}
                      size="small"
                      sx={{
                        color: "var(--ax-ink-2)",
                        "&:hover": {
                          color: "var(--ax-red)",
                          bgcolor: "var(--ax-red-soft)",
                        },
                      }}
                    >
                      {deletingProjectId === project.id ? (
                        <CircularProgress size={16} />
                      ) : (
                        <DeleteOutlineOutlinedIcon fontSize="small" />
                      )}
                    </IconButton>
                  </Stack>
                </div>
              </div>
            ))}
          </div>
        )}

        {!loading && sortedProjects.length === 0 ? (
          <Box className="ax-card" sx={{ p: 5, textAlign: "center" }}>
            <Typography
              sx={{
                fontFamily: "var(--ax-font-heading)",
                fontWeight: 500,
                fontSize: "1.05rem",
                color: "var(--ax-ink)",
              }}
            >
              No projects found
            </Typography>
            <Typography
              sx={{
                mt: 1,
                color: "var(--ax-ink-2)",
                fontSize: "0.86rem",
              }}
            >
              Create your first project and it will be stored in the backend.
            </Typography>
          </Box>
        ) : null}

        <Dialog
          open={open}
          onClose={closeDialog}
          fullWidth
          maxWidth="sm"
          className="ax-dialog"
        >
          <DialogTitle>
            {editingId ? "Edit Project" : "New Project"}
          </DialogTitle>
          <DialogContent>
            <Stack spacing={2.25} sx={{ pt: 1 }}>
              <TextField
                label="Project Name"
                value={form.name}
                size="small"
                onChange={(event) =>
                  setForm((previous) => ({
                    ...previous,
                    name: event.target.value,
                  }))
                }
              />
              <TextField
                label="Project Code"
                value={form.code}
                size="small"
                onChange={(event) =>
                  setForm((previous) => ({
                    ...previous,
                    code: event.target.value,
                  }))
                }
              />
              <TextField
                label="Description"
                multiline
                minRows={3}
                size="small"
                value={form.description}
                onChange={(event) =>
                  setForm((previous) => ({
                    ...previous,
                    description: event.target.value,
                  }))
                }
              />
              <TextField
                label="Roles"
                size="small"
                helperText="Comma separated, e.g. DEV-ADMIN, VIEW, DEV-TL"
                value={form.roles}
                onChange={(event) =>
                  setForm((previous) => ({
                    ...previous,
                    roles: event.target.value,
                  }))
                }
              />
            </Stack>
          </DialogContent>
          <DialogActions sx={{ px: 3, pb: 3 }}>
            <Button onClick={closeDialog} disabled={saving} variant="outlined">
              Cancel
            </Button>
            <Button onClick={handleSave} variant="contained" disabled={saving}>
              {saving ? "Saving..." : "Save Project"}
            </Button>
          </DialogActions>
        </Dialog>
      </div>
      <ToastContainer />
    </>
  );
}

export default Projects;
